/**
 * Reconciliation card — compares the sum of `infra.cloud_run_cost_in_currency`
 * across recent audit_cost_summary rows against the GCP billing export's
 * Cloud Run line items for the same window, per lane (request-based worker,
 * instance-based Job). Red reads "Attribution failure (Job lane)" when the Job
 * lane has tracker rows without billing rows or billing rows without tracker
 * rows. Inconclusive when the billing export is unavailable.
 *
 * Drift bands (green within 10%, yellow 10–20%, red beyond 20%) — source: `RECONCILE_PASS_BAND` / `RECONCILE_WARN_BAND` in `packages/platform-health/src/audit-cost-reconcile.ts`.
 *
 * Bands and lane states come from `reconcileAuditLaneCost` in
 * `@a7y/platform-health` (`audit-cost-reconcile.ts`), the module the
 * route handler, `scripts/reconcile-cloud-run-cost.cjs` and the Monday Slack
 * alert share; the labels below say only what that computation establishes.
 */
import { AlertCircle, AlertTriangle, CheckCircle, HelpCircle } from "lucide-react";
import { ViewQueryButton } from "./ViewQueryButton";
import { formatUsd } from "./format";
import type { ApiEnvelope, ReconcileData } from "./types";

interface ReconciliationCardProps {
  envelope: ApiEnvelope<ReconcileData> | undefined;
  loading: boolean;
}

function statusBadge(
  status: ReconcileData["status"],
  jobLaneState: ReconcileData["job_lane_state"]
) {
  switch (status) {
    case "pass":
      return {
        icon: <CheckCircle className="h-5 w-5 text-emerald-500" />,
        cardClass: "border-emerald-300 dark:border-emerald-700",
        label: "Within 10%",
      };
    case "warn":
      return {
        icon: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
        cardClass: "border-yellow-300 dark:border-yellow-700",
        label: "10–20% drift",
      };
    case "fail":
      return {
        icon: <AlertCircle className="h-5 w-5 text-red-500" />,
        cardClass: "border-red-300 dark:border-red-700",
        label:
          jobLaneState === "one_sided_tracker" || jobLaneState === "one_sided_billing"
            ? "Attribution failure (Job lane)"
            : ">20% drift",
      };
    case "inconclusive":
      return {
        icon: <HelpCircle className="h-5 w-5 text-zinc-400" />,
        cardClass: "border-zinc-300 dark:border-zinc-700",
        label: "Inconclusive",
      };
  }
}

/** Wording is limited to what `reconcileAuditLaneCost` establishes for each state. */
function jobLaneStateBadge(state: ReconcileData["job_lane_state"]) {
  switch (state) {
    case "inactive":
      return {
        label: "Job lane: no tracker rows, no billing rows matched",
        className: "text-zinc-500",
      };
    case "active":
      return {
        label: "Job lane: tracker and billing rows both present",
        className: "text-emerald-600 dark:text-emerald-400",
      };
    case "active_pending_billing":
      return {
        label: "Job lane: tracker rows present, billing not yet exported",
        className: "text-yellow-600 dark:text-yellow-400",
      };
    case "one_sided_tracker":
      return {
        label: "Job lane: tracker rows present, no billing rows matched",
        className: "text-red-600 dark:text-red-400",
      };
    case "one_sided_billing":
      return {
        label: "Job lane: billing rows present, no tracker rows",
        className: "text-red-600 dark:text-red-400",
      };
  }
}

interface LaneRowProps {
  testId: string;
  name: string;
  trackerCost: number;
  trackerRows: number;
  billingCost: number | null;
  billingRows: number | null;
  ratio: number | null;
}

function LaneRow({
  testId,
  name,
  trackerCost,
  trackerRows,
  billingCost,
  billingRows,
  ratio,
}: LaneRowProps) {
  return (
    <tr data-testid={testId} className="border-t border-zinc-100 dark:border-zinc-800">
      <th scope="row" className="py-1 pr-3 text-left font-medium text-zinc-700 dark:text-zinc-200">
        {name}
      </th>
      <td className="py-1 pr-3 text-zinc-900 dark:text-zinc-100">{formatUsd(trackerCost)}</td>
      <td className="py-1 pr-3 text-zinc-900 dark:text-zinc-100">
        {billingCost === null ? "—" : formatUsd(billingCost)}
      </td>
      <td className="py-1 pr-3 text-zinc-900 dark:text-zinc-100">
        {ratio === null ? "—" : ratio.toFixed(3)}
      </td>
      <td className="py-1 text-zinc-500">
        {trackerRows} tracker / {billingRows ?? "—"} billing
      </td>
    </tr>
  );
}

export function ReconciliationCard({ envelope, loading }: ReconciliationCardProps) {
  const data = envelope?.data;
  const config = data ? statusBadge(data.status, data.job_lane_state) : null;

  return (
    <section
      className={`rounded-lg border bg-white dark:bg-zinc-900 p-4 ${config?.cardClass ?? "border-zinc-200 dark:border-zinc-700"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h2 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            Cloud Run cost reconciliation
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Sum of per-audit cloud_run cost vs GCP billing export ({data?.hours ?? 24}h).
          </p>
        </div>
        {config && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-700 dark:text-zinc-200">
            {config.icon}
            {config.label}
          </span>
        )}
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-zinc-500">loading…</div>
      ) : data ? (
        <div>
          <div className="grid grid-cols-3 gap-3 mb-2">
            <div>
              <div className="text-xs text-zinc-500">audit_cost</div>
              <div className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {formatUsd(data.audit_cost)}
              </div>
              <div className="text-[10px] text-zinc-500">{data.audit_rows} rows</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">billing_cost</div>
              <div className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {data.billing_cost === null ? "—" : formatUsd(data.billing_cost)}
              </div>
              <div className="text-[10px] text-zinc-500">
                {data.billing_rows ?? "—"} rows
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">ratio</div>
              <div className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                {data.ratio === null ? "—" : data.ratio.toFixed(3)}
              </div>
            </div>
          </div>
          <table className="w-full text-xs mb-2" data-testid="reconcile-lanes">
            <thead>
              <tr className="text-left text-zinc-500">
                <th className="pb-1 pr-3 font-normal">Lane</th>
                <th className="pb-1 pr-3 font-normal">Tracked</th>
                <th className="pb-1 pr-3 font-normal">Billed</th>
                <th className="pb-1 pr-3 font-normal">Ratio</th>
                <th className="pb-1 font-normal">Rows</th>
              </tr>
            </thead>
            <tbody>
              <LaneRow
                testId="reconcile-lane-worker"
                name="Worker"
                trackerCost={data.worker_tracker_cost}
                trackerRows={data.worker_tracker_rows}
                billingCost={data.worker_billing_cost}
                billingRows={data.worker_billing_rows}
                ratio={data.worker_ratio}
              />
              <LaneRow
                testId="reconcile-lane-job"
                name="Job"
                trackerCost={data.job_tracker_cost}
                trackerRows={data.job_tracker_rows}
                billingCost={data.job_billing_cost}
                billingRows={data.job_billing_rows}
                ratio={data.job_ratio}
              />
            </tbody>
          </table>
          <p
            data-testid="reconcile-job-state"
            className={`text-xs font-medium mb-1 ${jobLaneStateBadge(data.job_lane_state).className}`}
          >
            {jobLaneStateBadge(data.job_lane_state).label}
          </p>
          <p data-testid="reconcile-job-coverage-note" className="text-xs text-zinc-500 mb-2">
            {data.job_coverage_note}
          </p>
          {data.findings.length > 0 && (
            <ul data-testid="reconcile-findings" className="list-disc pl-4 mb-2 text-xs text-zinc-700 dark:text-zinc-200">
              {data.findings.map((finding) => (
                <li key={finding}>{finding}</li>
              ))}
            </ul>
          )}
          <p className="text-xs text-zinc-500 italic">{data.note}</p>
          <p data-testid="reconcile-residual-note" className="text-xs text-zinc-500 italic mt-1">
            {data.residual_note}
          </p>
        </div>
      ) : null}

      <div className="mt-3">
        <ViewQueryButton query={envelope?.query} />
      </div>
    </section>
  );
}
